import React from 'react';
import { RatingStars } from '@/lib/components/ui/rating-stars';
import { RxStarFilled } from 'react-icons/rx';
import Tooltip from 'rc-tooltip';
import type { User } from 'modules/User/models/User';
import { formatDecimal } from 'lib/helpers/strings';
import { RatingBreakdownTooltip } from './RatingBreakdownTooltip';

export type RatingDisplayProps = {
  user: User;
  size?: 'sm' | 'md';
  showLabel?: boolean;
  compact?: boolean;
};

const getStarColor = (rating: number) => {
  if (rating < 3) return 'text-error';
  if (rating < 4) return 'text-warning';
  return 'text-yellow-500';
};

export function RatingDisplay({ user, size = 'sm', showLabel = true, compact = false }: RatingDisplayProps) {
  if (!user?.rating?.average) return null;

  const { average, count, responsiveness, relevance, reliability } = user.rating;
  const starColor = getStarColor(average);

  const tooltipContent = (
    <RatingBreakdownTooltip
      average={average}
      count={count}
      responsiveness={responsiveness}
      relevance={relevance}
      reliability={reliability}
      userId={user._id as string}
    />
  );

  if (compact) {
    return (
      <Tooltip placement="bottom" trigger={['hover']} overlay={tooltipContent}>
        <div className="flex items-center gap-1 cursor-help">
          <RxStarFilled className={starColor} />
          <span className="text-xs font-bold">{formatDecimal(average)}</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip placement="bottom" trigger={['hover']} overlay={tooltipContent}>
      <div className="flex items-center gap-1.5 cursor-help">
        <RatingStars value={average} readonly size={size} />
        {showLabel && <span className="text-sm text-gray-600">{formatDecimal(average)}</span>}
      </div>
    </Tooltip>
  );
}
